#!/usr/bin/env node
import { spawn } from "node:child_process";
import { promises as fs } from "node:fs";
import os from "node:os";
import path from "node:path";
import { parseArgs } from "node:util";
import { SessionStore } from "./parser.ts";
import { createStatsServer } from "./server.ts";

const DEFAULT_PORT = 4789;

const HELP = `pi-session-stats — 分析 Pi session 的 token 用量

用法:
  pi-session-stats [选项]

选项:
  -p, --port <端口>        监听端口 (默认: ${DEFAULT_PORT})
      --host <地址>        监听地址 (默认: 127.0.0.1)
  -d, --dir <目录>         session 目录 (默认: ~/.pi/agent/sessions)
      --open               启动后自动在浏览器中打开
  -h, --help               显示帮助
`;

function expandHome(input: string): string {
  if (input === "~") return os.homedir();
  if (input.startsWith("~/")) return path.join(os.homedir(), input.slice(2));
  return input;
}

function openBrowser(url: string): void {
  const isWsl = Boolean(process.env.WSL_DISTRO_NAME) ||
    (process.platform === "linux" &&
      os.release().toLowerCase().includes("microsoft"));
  const candidates =
    process.platform === "darwin"
      ? ["open"]
      : isWsl
        ? ["wslview", "xdg-open"]
        : ["xdg-open"];
  for (const cmd of candidates) {
    try {
      const child = spawn(cmd, [url], { detached: true, stdio: "ignore" });
      child.on("error", () => {});
      child.unref();
      return;
    } catch {
      // try next candidate
    }
  }
}

async function main(): Promise<void> {
  let args;
  try {
    args = parseArgs({
      options: {
        port: { type: "string", short: "p" },
        host: { type: "string" },
        dir: { type: "string", short: "d" },
        open: { type: "boolean" },
        help: { type: "boolean", short: "h" },
      },
    });
  } catch (error) {
    console.error(error instanceof Error ? error.message : String(error));
    console.error(HELP);
    process.exit(1);
  }

  if (args.values.help) {
    console.log(HELP);
    return;
  }

  const port = args.values.port ? Number(args.values.port) : DEFAULT_PORT;
  if (!Number.isInteger(port) || port <= 0 || port > 65535) {
    console.error(`无效端口: ${args.values.port}`);
    process.exit(1);
  }
  const host = args.values.host ?? "127.0.0.1";
  const dir = path.resolve(expandHome(args.values.dir ?? "~/.pi/agent/sessions"));

  try {
    const stat = await fs.stat(dir);
    if (!stat.isDirectory()) throw new Error("not a directory");
  } catch {
    console.error(`session 目录不存在: ${dir}`);
    process.exit(1);
  }

  console.log(`正在扫描 session 目录: ${dir}`);
  const store = new SessionStore(dir);
  const { server } = await createStatsServer(store);

  server.on("error", (error: NodeJS.ErrnoException) => {
    if (error.code === "EADDRINUSE") {
      console.error(`端口 ${port} 已被占用，请用 --port 换一个端口。`);
    } else {
      console.error(error.message);
    }
    process.exit(1);
  });

  server.listen(port, host, () => {
    const address = server.address();
    const actualPort = typeof address === "object" && address ? address.port : port;
    const url = `http://${host === "0.0.0.0" ? "127.0.0.1" : host}:${actualPort}`;
    console.log(`\n✔ 已启动，在浏览器中访问: ${url}\n按 Ctrl+C 停止。`);
    if (args.values.open) openBrowser(url);
  });

  const shutdown = (): void => {
    server.close(() => process.exit(0));
  };
  process.on("SIGINT", shutdown);
  process.on("SIGTERM", shutdown);
}

main().catch((error: unknown) => {
  console.error(error instanceof Error ? error.message : String(error));
  process.exit(1);
});
